import type { RequestOptions } from "@@/plugin-request/request";
import type { RequestConfig } from "@umijs/max";
import { message, notification } from "antd";

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number; // 0 表示成功
  data: any;
  msg?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: res => {
      const { code, data, msg, showType } = res as unknown as ResponseStructure;
      if (code !== 0) {
        const error: any = new Error(msg || "请求失败");
        error.name = "BizError";
        error.info = { errorCode: code, errorMessage: msg, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === "BizError") {
        const errorInfo: any = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`服务器错误`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // 检查是否是业务错误（从响应拦截器返回的 Promise.reject）
        if (error.data?.code !== undefined && error.data?.code !== 0) {
          const { msg, code } = error.data;

          // 处理 401 未授权错误
          if (code === 401) {
            const { history } = require("@umijs/max");
            const { location } = history;
            if (
              !["/user/login", "/user/register", "/user/register-result"].includes(
                location.pathname,
              )
            ) {
              const currentPath = `${location.pathname}${location.search}${location.hash}`;
              const redirect = encodeURIComponent(currentPath);
              // 清除本地存储的 token
              localStorage.removeItem("token");
              localStorage.removeItem("admin");
              history.replace(`/user/login?redirect=${redirect}`);
            }
          } else {
            message.error(msg || `请求错误(${code})`);
          }
        } else {
          message.error("None response! Please retry.");
        }
      } else {
        // 发送请求时出了点问题
        message.error("Request error, please retry.");
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      // 如果有 token 缓存，统一增加 authorization 请求头
      const token = localStorage.getItem("token");
      if (token) {
        config.headers = {
          ...config.headers,
          authorization: `Bearer ${token}`,
        };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    response => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as any;

      // 如果 code 为 0，直接返回 data 数据
      if (data?.code === 0) {
        response.data = data.data;
        return response;
      } else if (data?.code !== undefined && data?.code !== 0) {
        // code 非 0 时，返回 Promise.reject 触发错误流程
        // 保持 response.data 为完整的响应数据，供 errorThrower 使用
        return Promise.reject(response);
      }
      return response;
    },
  ],
};
